import React, { useState, useEffect } from "react";
import {
  Container,
  Alert,
  Modal,
  Button,
  Table,
  Row,
  Col,
  Form,
  Badge,
} from "react-bootstrap";
import AcknowledgmentPopup from "../../../utils/AcknowledgmentPopup";
import api from "../../../api/axios";
import { formatCurrency } from "../../../utils/formatCurrency";
import "../../../assets/css/planSelection.css";
import { companyID } from "../../../auth";
import {
  checkSubscriptionExpiry,
  fetchCompanySubscription,
} from "../../../services/SubscriptionService";
import { useNavigate } from "react-router-dom";
import { IoMdCheckmarkCircle, IoMdCloseCircle } from "react-icons/io";
import { initiatePayment, generatePaymentToken } from "../../../services/paymentServices";
import config from "../../../config";

const PlanSelection = ({ isSubscriptionExpired }) => {
  const company_id = companyID();
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [sortedModules, setSortedModules] = useState([]);
  const [focusedPlanId, setFocusedPlanId] = useState(null);
  const [error, setError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [modalSelections, setModalSelections] = useState({
    duration_id: null,
    option_id: null,
    user_count: 1,
    total_cost: 0,
  });
  const navigate = useNavigate();

  // PROD
  const redirectURL = "https://hrglobes.com/";

  const fetchPlans = async () => {
    try {
      const response = await api.get("plans");
      if (response.status === 200) {
        const fetchedPlans = response.data.data ?? [];
        setPlans(fetchedPlans);
        setSortedModules(
          fetchedPlans.length > 0 ? sortFeatures(fetchedPlans) : []
        );
      }
    } catch (err) {
      setError("Failed to load plans. Please try again.");
    }
  };

  const fetchAllFeatures = async () => {
    try {
      const response = await api.get("plans/features/all");
      if (response.status === 200) {
        setModules(response.data.data ?? []);
      }
    } catch (error) {
      console.error("Error fetching all features:", error);
    }
  };

  useEffect(() => {
    fetchAllFeatures();
    fetchPlans();
  }, []);

  const checkSubscription = async () => {
    try {
      const subscription = await fetchCompanySubscription(company_id);
      const expired = checkSubscriptionExpiry(subscription);
      localStorage.setItem("isSubscriptionExpired", JSON.stringify(expired));
      navigate("../");
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (company_id) {
      checkSubscription();
    }
  }, []);

  function isSpecialPriceValid(
    specialPriceStartDate,
    specialPriceEndDate,
    checkDate = new Date()
  ) {
    const pad = (n) => String(n).padStart(2, "0");
    const formattedCheckDate = [
      checkDate.getFullYear(),
      pad(checkDate.getMonth() + 1),
      pad(checkDate.getDate()),
    ].join("-");

    return (
      formattedCheckDate >= specialPriceStartDate &&
      formattedCheckDate <= specialPriceEndDate
    );
  }

  function sortFeatures(plansData) {
    const allFeatures = [];
    const featureMap = new Map();

    plansData.forEach((plan) => {
      plan.features.forEach((feature) => {
        const featureKey = feature.feature_name;
        if (!featureMap.has(featureKey)) {
          featureMap.set(featureKey, {
            name: featureKey,
            enabled: 0,
            total: 0,
          });
        }
        featureMap.get(featureKey).total++;
        if (feature.is_enabled) {
          featureMap.get(featureKey).enabled++;
        }
      });
    });

    featureMap.forEach((value, key) => {
      allFeatures.push(value);
    });
    allFeatures.sort((a, b) => b.enabled - a.enabled);

    return allFeatures.map((feature) => feature.name);
  }

  // Group features by category
  const groupFeaturesByCategory = (features) => {
    return features.reduce((acc, feature) => {
      const category = feature.category || "Other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(feature);
      return acc;
    }, {});
  };

  // Open modal with selected plan (without duration initially)
  const openPlanModal = (planId) => {
    const plan = plans.find((p) => p.plan_id === planId);
    setSelectedPlan(plan);

    // Initialize modal with first duration and option
    const firstDuration = plan.plan_duration?.[0];
    const firstOption = plan.options?.[0];
    const initialUserCount = firstOption?.max_persons || 1; // Use max_persons instead of min_persons

    setSelectedDuration(firstDuration);

    setModalSelections({
      duration_id: firstDuration?.period_id || null,
      option_id: firstOption?.option_id || null,
      user_count: initialUserCount,
      total_cost: calculateModalCost(firstDuration, initialUserCount),
    });

    setShowPlanModal(true);
  };

  // Calculate cost for modal
  const calculateModalCost = (duration, userCount) => {
    if (!duration || !userCount) return 0;

    const {
      special_price,
      base_price_per_person,
      special_price_start_date,
      special_price_end_date,
    } = duration;

    const isSpecialValid =
      special_price &&
      special_price_start_date &&
      special_price_end_date &&
      isSpecialPriceValid(special_price_start_date, special_price_end_date);

    const pricePerUser =
      isSpecialValid && base_price_per_person > special_price
        ? special_price
        : base_price_per_person;

    return (pricePerUser * userCount * duration.duration).toFixed(2);
  };

  // Handle duration change in modal
  const handleModalDurationChange = (durationId) => {
    const duration = selectedPlan.plan_duration.find(
      (d) => d.period_id === parseInt(durationId)
    );
    setSelectedDuration(duration);
    const newCost = calculateModalCost(duration, modalSelections.user_count);

    setModalSelections((prev) => ({
      ...prev,
      duration_id: parseInt(durationId),
      total_cost: newCost,
    }));
  };

  // Handle option (user range) change in modal
  const handleModalOptionChange = (optionId) => {
    const option = selectedPlan.options.find(
      (o) => o.option_id === parseInt(optionId)
    );
    const userCount = option?.max_persons || 1; // Use max_persons instead of min_persons
    const newCost = calculateModalCost(selectedDuration, userCount);

    setModalSelections((prev) => ({
      ...prev,
      option_id: parseInt(optionId),
      user_count: userCount,
      total_cost: newCost,
    }));
  };

  // Handle payment processing from modal
  const handleProcessPayment = async () => {
    if (
      !selectedPlan ||
      !modalSelections.duration_id ||
      !modalSelections.user_count
    ) {
      setError("Please enter number of users.");
      return;
    }

    try {
      setError("");
      const res = await generatePaymentToken({
        paymentDetails: { 
          plan_id: selectedPlan?.plan_id,
          option_id: modalSelections?.option_id,
          plan_period: modalSelections?.duration_id,
          user_count: modalSelections?.user_count,
          total_cost: modalSelections?.total_cost, 
        }, 
        redirectUrl : config.APP_URL + "payment-confirmation",
        company_id: company_id,
      });

      if (res && res.data.redirectUrl) {
        window.location.href = res.data.redirectUrl;
      } else {
        setAlertMessage("Failed to initiate subscription. Please try again.");
        setAlertVariant("danger");
        setShowAlert(true);
      }
    } catch (err) {
      console.log(err.message);
      setAlertMessage("Failed to initiate subscription. Please try again.");
      setAlertVariant("danger");
      setShowAlert(true);
    }
  };

  const renderModuleIcon = (isEnabled) => {
    if (isEnabled) {
      return (
        <span className="text-success fs-5">
          <IoMdCheckmarkCircle />
        </span>
      );
    }
    return (
      <span className="text-danger fs-5">
        <IoMdCloseCircle />
      </span>
    );
  };

  return (
    <section
      id="pricing"
      className={`pricing section ${isSubscriptionExpired ? "mt-5 pt-5" : ""}`}
      style={{ position: "relative", minHeight: "100vh" }}
    >
      <Container className="section-title text-center">
        <h2>Choose Your Plan</h2>
        <p className="para-text pb-2">
          Select the perfect plan for your organization
        </p>
      </Container>

      {error && (
        <Container>
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        </Container>
      )}

      <div className="px-1">
        <h2 className="text-center fw-bold mt-5 mb-0">
          Your HR Partner from Day One to Expansion
        </h2>
        <p className="text-center text-muted mb-5">
          From startup to scale-up, grow your business your way
        </p>
        <div className="table-responsive pricing-table-wrapper">
          {plans.length === 0 ? (
            <div className="text-center my-5">Loading plans...</div>
          ) : (
            <Table bordered className="pricing-table mb-0">
              <thead>
                <tr>
                  <th className="sticky-col bg-white align-middle" rowSpan="2">
                    Features
                    <br />
                    <span className="fs-6 fw-light">(Per user / month)</span>
                  </th>
                  {plans.map((plan) => (
                    <th
                      key={plan.plan_id}
                      colSpan="3"
                      className={`plan-header text-center ${
                        focusedPlanId === plan.plan_id ? "focused-plan" : ""
                      }`}
                      onMouseEnter={() => setFocusedPlanId(plan.plan_id)}
                      onMouseLeave={() => setFocusedPlanId(null)}
                      onClick={() => openPlanModal(plan.plan_id)}
                      style={{ cursor: "pointer" }}
                    >
                      {plan.most_popular && (
                        <p className="most-popular">Most popular</p>
                      )}
                      <h5 className="mb-1">{plan.plan_name}</h5>
                      <small className="text-muted">{plan.description}</small>
                    </th>
                  ))}
                </tr>
                <tr>
                  {plans.map((plan) =>
                    plan.plan_duration?.map((pd, idx) => {
                      const {
                        period_id,
                        period_title,
                        base_price_per_person,
                        special_price,
                        special_price_start_date,
                        special_price_end_date,
                        currency,
                      } = pd;

                      const isFirst = idx === 0;
                      const isLast = idx === plan.plan_duration.length - 1;
                      const isFreePlan = plan.plan_id === 1;

                      const borderClass =
                        !isFreePlan && (isFirst || isLast)
                          ? isFirst
                            ? "plan-duration-border-left"
                            : "plan-duration-border-right"
                          : "";

                      return isFreePlan ? (
                        <th
                          key={`${plan.plan_id}-${period_id}`}
                          colSpan={3}
                          className={`text-center small ${
                            focusedPlanId === plan.plan_id
                              ? "focused-plan-duration"
                              : ""
                          }`}
                          onMouseEnter={() => setFocusedPlanId(plan.plan_id)}
                          onMouseLeave={() => setFocusedPlanId(null)}
                          onClick={() => openPlanModal(plan.plan_id)}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="fw-medium">
                            Free for {pd.duration} months
                          </span>
                        </th>
                      ) : (
                        <th
                          key={`${plan.plan_id}-${period_id}`}
                          className={`text-center small ${
                            focusedPlanId === plan.plan_id && borderClass
                          }`}
                          onMouseEnter={() => setFocusedPlanId(plan.plan_id)}
                          onMouseLeave={() => setFocusedPlanId(null)}
                          onClick={() => openPlanModal(plan.plan_id)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="px-1">
                            {special_price &&
                              isSpecialPriceValid(
                                special_price_start_date,
                                special_price_end_date
                              ) &&
                              base_price_per_person > special_price && (
                                <p className="discount-badge">
                                  {Math.round(
                                    ((base_price_per_person - special_price) /
                                      base_price_per_person) *
                                      100
                                  )}
                                  % OFF
                                </p>
                              )}
                            <strong className="d-block">{period_title}</strong>
                            <span className="text-muted fs-sm fw-lighter">
                              ({pd.duration} months)
                            </span>
                            <br />
                            {special_price &&
                            isSpecialPriceValid(
                              special_price_start_date,
                              special_price_end_date
                            ) &&
                            base_price_per_person > special_price ? (
                              <>
                                <span className="text-muted text-decoration-line-through d-block">
                                  {currency} {base_price_per_person}
                                </span>
                                <span className="text-success fw-bold fs-6">
                                  {currency} {special_price}
                                </span>
                              </>
                            ) : (
                              <span className="fw-medium">
                                {currency} {base_price_per_person ?? "—"}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })
                  )}
                </tr>
              </thead>

              <tbody>
                {sortedModules.map((modName, idx) => {
                  const mod = modules.find((m) => m.name === modName);

                  return (
                    <tr key={mod?.id || modName}>
                      <td className="sticky-col bg-white fw-medium">
                        <span className="me-2 text-warning fs-5">●</span>
                        {modName}
                      </td>
                      {plans.map((plan) => {
                        const feature = plan.features.find(
                          (f) => f.feature_name === modName
                        );
                        const isEnabled = feature?.is_enabled ?? false;
                        return (
                          <td
                            key={plan.plan_id + "-" + modName}
                            className={`text-center align-middle ${
                              focusedPlanId === plan.plan_id
                                ? "focused-plan-feature"
                                : ""
                            } ${
                              sortedModules.length - 1 === idx &&
                              focusedPlanId === plan.plan_id
                                ? "focused-plan-bottom"
                                : ""
                            }`}
                            onMouseEnter={() => setFocusedPlanId(plan.plan_id)}
                            onMouseLeave={() => setFocusedPlanId(null)}
                            colSpan={3}
                          >
                            {renderModuleIcon(isEnabled)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      {/* Plan Selection Modal */}
      <Modal
        show={showPlanModal}
        onHide={() => setShowPlanModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Configure Your {selectedPlan?.plan_name} Plan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPlan && selectedDuration && (
            <Container>
              <Row>
                <Col md={6}>
                  <h5 className="mb-3">Plan Details</h5>
                  <div className="mb-3">
                    <strong>Plan:</strong> {selectedPlan.plan_name}
                    {selectedPlan.most_popular && (
                      <Badge bg="warning" className="ms-2">
                        Most Popular
                      </Badge>
                    )}
                  </div>
                  <div className="mb-3">
                    <strong>Description:</strong>{" "}
                    {selectedPlan.plan_description || "Premium HR solution"}
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Select Duration</Form.Label>
                    <Form.Select
                      value={modalSelections.duration_id || ""}
                      onChange={(e) =>
                        handleModalDurationChange(e.target.value)
                      }
                    >
                      {selectedPlan.plan_duration?.map((duration) => {
                        const hasSpecialPrice =
                          duration.special_price &&
                          isSpecialPriceValid(
                            duration.special_price_start_date,
                            duration.special_price_end_date
                          ) &&
                          duration.base_price_per_person >
                            duration.special_price;

                        return (
                          <option
                            key={duration.period_id}
                            value={duration.period_id}
                          >
                            {duration.period_title} ({duration.duration} months)
                            -{" "}
                            {hasSpecialPrice
                              ? `${formatCurrency(
                                  duration.special_price
                                )}/user/month (Save ${Math.round(
                                  ((duration.base_price_per_person -
                                    duration.special_price) /
                                    duration.base_price_per_person) *
                                    100
                                )}%)`
                              : `${formatCurrency(
                                  duration.base_price_per_person
                                )}/user/month`}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>

                  {selectedPlan.options && selectedPlan.options.length > 0 && (
                    <Form.Group className="mb-3">
                      <Form.Label>Select User Range</Form.Label>
                      <Form.Select
                        value={modalSelections.option_id || ""}
                        onChange={(e) =>
                          handleModalOptionChange(e.target.value)
                        }
                      >
                        {selectedPlan.options.map((option) => (
                          <option
                            key={option.option_id}
                            value={option.option_id}
                          >
                            {option.min_persons === 0
                              ? "Up to"
                              : `${option.min_persons} to`}{" "}
                            {option.max_persons} users
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}

                  {/* Remove the Number of Users Form.Group */}
                </Col>

                <Col md={6}>
                  <h5 className="mb-3">Cost Breakdown</h5>
                  {(() => {
                    const isSpecialValid =
                      selectedDuration.special_price &&
                      isSpecialPriceValid(
                        selectedDuration.special_price_start_date,
                        selectedDuration.special_price_end_date
                      );
                    const hasDiscount =
                      isSpecialValid &&
                      selectedDuration.base_price_per_person >
                        selectedDuration.special_price;
                    const pricePerUser = hasDiscount
                      ? selectedDuration.special_price
                      : selectedDuration.base_price_per_person;

                    return (
                      <>
                        <div className="mb-2">
                          <strong>Duration:</strong>{" "}
                          {selectedDuration.period_title} (
                          {selectedDuration.duration} months)
                        </div>
                        <div className="mb-2">
                          <strong>Users:</strong> {modalSelections.user_count}
                        </div>
                        <div className="mb-2">
                          <strong>Price per user per month:</strong>{" "}
                          {hasDiscount ? (
                            <>
                              <span className="text-decoration-line-through text-muted">
                                {formatCurrency(
                                  selectedDuration.base_price_per_person
                                )}
                              </span>{" "}
                              <span className="text-success">
                                {formatCurrency(selectedDuration.special_price)}
                              </span>
                              <Badge bg="success" className="ms-2">
                                Save{" "}
                                {Math.round(
                                  ((selectedDuration.base_price_per_person -
                                    selectedDuration.special_price) /
                                    selectedDuration.base_price_per_person) *
                                    100
                                )}
                                %
                              </Badge>
                            </>
                          ) : (
                            formatCurrency(pricePerUser)
                          )}
                        </div>
                        <hr />
                        <div className="mb-2">
                          <strong>Calculation:</strong>
                          <br />
                          {formatCurrency(pricePerUser)} ×{" "}
                          {modalSelections.user_count} users ×{" "}
                          {selectedDuration.duration} months
                        </div>
                      </>
                    );
                  })()}
                  <div className="alert alert-info">
                    <h4 className="mb-0">
                      Total: {formatCurrency(modalSelections.total_cost)}
                    </h4>
                  </div>
                </Col>
              </Row>
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          {showAlert && (
            <AcknowledgmentPopup
              popupVariant={alertVariant}
              popupMessage={alertMessage}
              popupIcon={
                alertVariant === "success"
                  ? "fa-regular fa-circle-check"
                  : "fa-solid fa-exclamation-circle"
              }
              onClose={() => setShowAlert(false)}
            />
          )}
          <Button variant="secondary" onClick={() => setShowPlanModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleProcessPayment}
            disabled={
              !modalSelections.user_count || modalSelections.user_count === 0
            }
          >
            Proceed to Payment
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default PlanSelection;
